import React, { memo, useContext } from 'react';
import {
  InteractionStepType,
  StepParamsForType,
  StepParamsForTypeWithStage,
  getCanAddFeedback,
  getCanViewEditFeedback,
} from '@scale/shared/types/chat';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import ContributorHumanFeedbackBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/ContributorHumanFeedbackBox';
import ContributorAutomatedFeedbackBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/ContributorAutomatedFeedbackBox';
import { Icon, Typography } from '@scale/scaleui';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { twMerge } from 'tailwind-merge';

type ContributorFeedbackProps<T extends InteractionStepType> = {
  className?: string;
  step?: (StepParamsForType<T> & StepParamsAuxiliary) | StepParamsForTypeWithStage<T>;
  isHidden?: boolean;
  placeholder?: string;
};

const ContributorFeedbackBox: React.FC<ContributorFeedbackProps<InteractionStepType>> = ({
  className,
  step,
  isHidden,
  placeholder,
}) => {
  const taskContext = useContext(ChatTaskContext);

  const feedback = useContributorFeedbackStore(s => s.feedback);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const canAddFeedback = getCanAddFeedback({
    reviewLevel,
    areCommentsReadOnly: taskContext.areCommentsReadOnly,
  });

  const canViewEditFeedback = getCanViewEditFeedback({
    feedback,
  });

  const shouldRenderUnifiedFeedback =
    taskContext.enableUnifiedFeedback &&
    !taskContext.feedbackCenterMode &&
    (canAddFeedback || canViewEditFeedback);

  if (!shouldRenderUnifiedFeedback) return null;

  const shouldShowHumanFeedback =
    taskContext.contextSource !== 'ChatBulkAudit' &&
    step?.type !== InteractionStepType.QualityMeasurement;

  return !isHidden && !placeholder && step ? (
    <div className={className}>
      {shouldShowHumanFeedback && <ContributorHumanFeedbackBox className="mb-5" step={step} />}
      <ContributorAutomatedFeedbackBox step={step} />
    </div>
  ) : placeholder ? (
    <NotificationBox className={className} placeholder={placeholder} />
  ) : (
    <div className={twMerge('flex lg:w-60 xl:w-80', className)} />
  );
};

type NotificationBoxProps = {
  className?: string;
  placeholder: string;
};

const NotificationBox: React.FC<NotificationBoxProps> = ({ className, placeholder }) => {
  return (
    <div
      className={twMerge(
        'flex flex-row lg:w-60 xl:w-80 p-1.5 gap-1.5 items-center rounded-md border border-neutral-200',
        className,
      )}
    >
      <Icon icon={['fas', 'info-circle']} />
      <Typography.Subtitle4>{placeholder}</Typography.Subtitle4>
    </div>
  );
};

export default memo(ContributorFeedbackBox);
